import {useMemo} from 'react';

import {useTranslation} from 'react-i18next';

import {MatchesPageActiveStatFilter} from '../companyMatchesListFilters';
import {inPipelineMatchCount} from '../utils/matchCountUtils';

import {BaseStatCard} from '../../../../../components/BaseStatCard/BaseStatCard';
import {StatsGridContainer} from '../../../../../components/StatsGridContainer/StatsGridContainer';
import {greenColors} from '../../../../../layouts/theme/designSystem';
import {MatchTotalCountPerStatus} from '../../../../../types/shared-recruiting-types';

interface Props {
  activeFilter?: MatchesPageActiveStatFilter;
  counts?: MatchTotalCountPerStatus;
  loading?: boolean;
  onAllMatchesClick?: () => void;
  onPendingCardClick?: () => void;
  onInProgressCardClick?: () => void;
}

export const MatchCountDisplay = ({
  activeFilter,
  counts,
  loading,
  onAllMatchesClick,
  onPendingCardClick,
  onInProgressCardClick,
}: Props) => {
  const {t} = useTranslation('translation', {keyPrefix: 'matchesPage.stats'});

  const metrics = useMemo(() => {
    if (!counts) {
      return {delivered: 0, pending: 0, inPipeline: 0};
    }
    let delivered = 0;
    Object.keys(counts).forEach((key: string) => {
      const v = counts[key as keyof MatchTotalCountPerStatus];
      if (v) {
        delivered += v;
      }
    });
    const inPipeline = inPipelineMatchCount(counts);
    const pending = delivered - inPipeline - counts.hired - counts.rejected;
    return {delivered, pending, inPipeline};
  }, [counts]);

  if (loading) {
    return <StatsGridContainer loading skeletonCount={3} />;
  }

  return (
    <StatsGridContainer>
      <BaseStatCard
        fillWidth
        active={activeFilter === 'all'}
        title={t('deliveredTitle')}
        value={metrics.delivered}
        onClick={onAllMatchesClick}
        valueSx={{color: greenColors[700], fontWeight: 700}}
      />
      <BaseStatCard
        fillWidth
        active={activeFilter === 'pending'}
        title={t('pendingTitle')}
        value={metrics.pending}
        highlight
        onClick={onPendingCardClick}
        valueSx={{color: greenColors[500], fontWeight: 600}}
      />
      <BaseStatCard
        fillWidth
        active={activeFilter === 'inProgress'}
        title={t('acceptedTitle')}
        value={metrics.inPipeline}
        onClick={onInProgressCardClick}
        valueSx={{color: greenColors[600], fontWeight: 600}}
      />
    </StatsGridContainer>
  );
};
